/**
 * AddPeopleModal — Marakin staff add people who ALREADY have an account
 * straight into a club (no invite, no email). 25 Sept 2026.
 * Backend: GET /api/platform/users/search · POST /api/platform/clubs/:id/members
 */
import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePlatformAuth } from '../../context/PlatformAuthContext';
import { X, Search, Loader, AlertCircle, CheckCircle2, UserPlus } from 'lucide-react';

export default function AddPeopleModal({ club, onClose }) {
  const { platformAxios } = usePlatformAuth();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [picked, setPicked] = useState({}); // user_id → display name
  const [role, setRole] = useState('supporter');
  const [clubMember, setClubMember] = useState(false);
  const [clubStaff, setClubStaff] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const { data: people, isFetching } = useQuery({
    queryKey: ['platform-user-search', club.id, debounced],
    queryFn: () => platformAxios.get('/users/search', { params: { q: debounced, clubId: club.id } }).then(r => r.data),
    enabled: debounced.length >= 2,
  });

  const addMutation = useMutation({
    mutationFn: () => platformAxios.post(`/clubs/${club.id}/members`, {
      userIds: Object.keys(picked), role, clubMember, clubStaff,
    }).then(r => r.data),
    onSuccess: (data) => {
      setResult(data);
      queryClient.invalidateQueries({ queryKey: ['platform-club-members', club.id] });
      queryClient.invalidateQueries({ queryKey: ['platform-clubs'] });
      queryClient.invalidateQueries({ queryKey: ['platform-user-search'] });
    },
    onError: (err) => setError(err.response?.data?.error || 'Could not add people'),
  });

  const toggle = (p) => {
    setPicked(prev => {
      const next = { ...prev };
      if (next[p.user_id]) delete next[p.user_id];
      else next[p.user_id] = [p.first_name, p.last_name].filter(Boolean).join(' ') || p.email;
      return next;
    });
  };

  const count = Object.keys(picked).length;
  const cardsOn = !!club.membership_cards_enabled;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content add-people-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add people to {club.name}</h2>
          <button className="btn-close" onClick={onClose}><X size={24} /></button>
        </div>

        {result ? (
          <div className="modal-form">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#1E4A35', fontWeight: 600 }}>
              <CheckCircle2 size={20} />
              <p style={{ margin: 0 }}>
                {result.added} added{result.restored ? ` · ${result.restored} restored or made admin` : ''}
                {result.unchanged ? ` · ${result.unchanged} already in` : ''}
              </p>
            </div>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#666', lineHeight: 1.5 }}>
              No email is sent. {club.name} appears in their club list next time they open the app.
            </p>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => { setResult(null); setPicked({}); setQuery(''); }}>
                Add more
              </button>
              <button type="button" className="btn-primary" onClick={onClose}>Done</button>
            </div>
          </div>
        ) : (
          <div className="modal-form">
            {error && <div className="error-box"><AlertCircle size={18} /><p>{error}</p></div>}

            <div className="form-group">
              <label>Find people with a Marakin account</label>
              <div className="add-people-search">
                <Search size={16} />
                <input
                  type="text" autoFocus placeholder="Name or email"
                  value={query} onChange={(e) => setQuery(e.target.value)}
                />
                {isFetching && <Loader size={15} style={{ animation: 'spin 1s linear infinite' }} />}
              </div>
            </div>

            {debounced.length >= 2 && (
              <div className="add-people-results">
                {(people || []).length === 0 && !isFetching && (
                  <p className="add-people-empty">No one found. They need to sign up to Marakin first.</p>
                )}
                {(people || []).map(p => {
                  const alreadyIn = p.in_club === 'in' && !(role === 'admin' && p.club_role !== 'admin');
                  const name = [p.first_name, p.last_name].filter(Boolean).join(' ') || p.email;
                  return (
                    <label key={p.user_id} className={`add-people-row${alreadyIn ? ' is-in' : ''}`}>
                      <input type="checkbox" disabled={alreadyIn} checked={!!picked[p.user_id]} onChange={() => toggle(p)} />
                      <span className="add-people-name">
                        <strong>{name}</strong>
                        <span>{p.email} · {p.club_count} {p.club_count === 1 ? 'club' : 'clubs'}</span>
                      </span>
                      {p.in_club === 'in' && <span className="add-people-tag">Already in{p.club_role === 'admin' ? ' · admin' : ''}</span>}
                      {p.in_club === 'archived' && <span className="add-people-tag">Archived — tick to restore</span>}
                    </label>
                  );
                })}
              </div>
            )}

            {count > 0 && (
              <p className="add-people-picked">
                <UserPlus size={14} /> {count} picked: {Object.values(picked).join(', ')}
              </p>
            )}

            <div className="form-group">
              <label>Add them as</label>
              <select value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="supporter">Community member</option>
                <option value="admin">Club admin</option>
              </select>
              <p className="add-people-hint">
                Anyone already in the club keeps their role — choosing Club admin only ever promotes.
              </p>
            </div>

            <div className="form-group">
              <label className="add-people-check">
                <input type="checkbox" checked={clubMember} onChange={(e) => setClubMember(e.target.checked)} />
                Club member (membership card)
              </label>
              <label className="add-people-check">
                <input type="checkbox" checked={clubStaff} onChange={(e) => setClubStaff(e.target.checked)} />
                Club staff (can scan cards)
              </label>
              <p className="add-people-hint">
                Adults only. {cardsOn ? '' : 'Membership cards are off for this club, so these only show once it switches them on.'}
              </p>
            </div>

            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
              <button type="button" className="btn-primary" disabled={!count || addMutation.isPending}
                onClick={() => { setError(''); addMutation.mutate(); }}>
                {addMutation.isPending ? 'Adding…' : count ? `Add ${count} ${count === 1 ? 'person' : 'people'}` : 'Add people'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
